import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useDebounce from '@/components/hooks/useDebounce';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { cn } from '@/lib/utils';
import { SiteOption, sitesQueryOptions } from '@/services/sites';

type SitesMultiComboboxProps = {
  value: SiteOption[];
  onChange: (sites: SiteOption[]) => void;
  placeholder?: string;
  className?: string;
};

export const SitesMultiCombobox = ({
  value,
  onChange,
  placeholder,
  className,
}: SitesMultiComboboxProps) => {
  const { t } = useTranslation();
  const [siteSearch, setSiteSearch] = useState('');
  const debouncedSearch = useDebounce(siteSearch);

  const { data: sites } = useQuery(
    sitesQueryOptions({ search: debouncedSearch }),
  );

  return (
    <Combobox
      items={sites}
      multiple
      value={value}
      onValueChange={(next: SiteOption[]) => {
        setSiteSearch('');
        onChange(next);
      }}
      itemToStringValue={(option) => option.id}
      itemToStringLabel={(option) => option.name}
    >
      <ComboboxChips className={cn('w-82', className)}>
        {value.map((site) => (
          <ComboboxChip key={site.id} aria-label={site.name}>
            {site.name}
          </ComboboxChip>
        ))}
        <ComboboxChipsInput
          placeholder={
            value.length === 0 ? (placeholder ?? t('site.selectSite')) : ''
          }
          value={siteSearch}
          onChange={(e) => setSiteSearch(e.target.value)}
        />
      </ComboboxChips>
      <ComboboxContent>
        <ComboboxEmpty>{t('site.noSitesFound')}</ComboboxEmpty>
        <ComboboxList>
          {(site: SiteOption) => (
            <ComboboxItem key={site.id} value={site}>
              {site.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};
